import { MarketPOI } from 'core/entities';
import type { URLEntity } from 'features/insights/types/url-types';
import { updateURL } from 'features/insights/store/actions/fetch-report/update-url';
import { PoiDatesRecord } from 'features/insights/types/dates-types';
import { PointOfInterestEntities } from '@placer-ui/types/src/entities/entity-unions';
import { getFeatureFlags } from 'core/flow-control';
import type { FilterConfigurationPayload } from '@placer-ui/types';
import { IUrlService } from '../../../../../infra';

type UpdateEntitiesMarketsConfig = {
    withoutCompetitor: boolean;
    dates?: PoiDatesRecord;
    reportEntities: PointOfInterestEntities[];
    entitiesFromURL: URLEntity[];
    urlServices?: IUrlService;
};

export const updateEntitiesMarketsConfig = ({
    reportEntities,
    entitiesFromURL,
    withoutCompetitor,
    dates,
    urlServices,
}: UpdateEntitiesMarketsConfig) => {
    const { enable_market_in_property } = getFeatureFlags();
    if (!enable_market_in_property) {
        return entitiesFromURL;
    }

    const areMarketsInProperty = reportEntities.some(MarketPOI.isMarketPOI);
    const hasMarketInPropertyConfig = entitiesFromURL.some(
        (poi) => poi.filter?.config?.market_in_property,
    );

    const shouldUpdateEntities =
        (areMarketsInProperty && !hasMarketInPropertyConfig) ||
        (!areMarketsInProperty && hasMarketInPropertyConfig);

    if (!shouldUpdateEntities) {
        return entitiesFromURL;
    }

    const createUpdatedEntity = (urlEntity: URLEntity, config?: FilterConfigurationPayload) => ({
        id: urlEntity.id,
        type: urlEntity.type,
        filter: {
            ...urlEntity.filter,
            config,
        },
    });

    const updatedEntitiesUrl = reportEntities.reduce<URLEntity[]>((acc, entity, idx) => {
        const urlEntity = entitiesFromURL[idx];

        if (MarketPOI.isMarketPOI(entity)) {
            acc.push(createUpdatedEntity(urlEntity, { market_in_property: true }));
        } else if (urlEntity?.filter?.config && 'market_in_property' in urlEntity.filter.config) {
            const config = urlEntity.filter.config?.ft_radius
                ? { ft_radius: urlEntity.filter.config.ft_radius }
                : undefined;
            acc.push(createUpdatedEntity(urlEntity, config));
        }

        return acc;
    }, []);

    updateURL(updatedEntitiesUrl, withoutCompetitor, dates, urlServices);

    return updatedEntitiesUrl;
};
